import { useMemo } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { useStoryblokProxy } from "./useStoryblokProxy";
import { NewsStory } from "../types/NewsTypes";
import {
  StoryblokSearchOptions,
  StoryblokSearchResponse,
  StoryblokSearchParams,
} from "../types/SearchTypes";
import { transformStoryblokToNews } from "../utils/storyblokTransforms";
import { languageEnglish } from "../i18n";
import { MINIMAL_SEARCH_CHARACTERS } from "../constants";

/**
 * Custom hook to search for stories in Storyblok using full-text search.
 *
 * This hook searches across story content, including titles, descriptions, and body text.
 * It uses Storyblok's search_term parameter for full-text search capabilities and filters
 * content by language using the language parameter to ensure users only see content
 * in their preferred language.
 *
 * @param options - The search options including search term and filters
 * @param options.searchTerm - The text to search for across story content
 * @param options.language - Optional language filter ('en' for English, undefined for Dutch default)
 * @param options.per_page - Number of results per page (default: 5)
 * @param abortRequest - Optional flag to abort the request
 *
 * @returns UseQueryResult containing search results
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useStoryblokSearch({
 *   searchTerm: "company policy",
 *   language: "en",
 *   per_page: 10
 * });
 * ```
 */
export const useStoryblokSearch = (
  searchOptions: StoryblokSearchOptions,
  abortRequest = false
): UseQueryResult<StoryblokSearchResponse> => {
  const { searchTerm = "", language, ...otherOptions } = searchOptions;

  const queryParams: StoryblokSearchParams = {
    search_term: searchTerm,
    resolve_relations: "brand,categories,cluster",
    sort_by: "content.date:desc",
    language: language,
    filter_query: {
      component: {
        in: "NewsArticle,page,Department",
      },
      storyTranslated: {
        is: language === languageEnglish ? true : null,
      },
    },
    ...otherOptions,
  };

  const storyblokData = useStoryblokProxy(
    queryParams,
    abortRequest ||
      !searchTerm.trim() ||
      searchTerm.trim().length < MINIMAL_SEARCH_CHARACTERS
  );

  const transformedData = useMemo<NewsStory[] | undefined>(() => {
    if (!storyblokData.data) return undefined;

    const { stories = [], rels = [] } = storyblokData.data;
    return transformStoryblokToNews(stories, rels);
  }, [storyblokData.data]);

  return {
    ...storyblokData,
    data: {
      storyData: transformedData || [],
      headers: storyblokData.data?.headers,
    },
  } as unknown as UseQueryResult<StoryblokSearchResponse>;
};
